import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { postDataAPI } from '../lib/api'
import Button from '../components/ui/Button'
import Alert from '../components/ui/Alert'

type ManagedArtist = {
  _id: string
  id?: string
  name: string
  email: string
  role: string
  profilePicture?: string
  followers?: number
  status?: string
}

type InvitationRequest = {
  _id: string
  id?: string
  artistId: string
  artistName: string
  artistEmail: string
  artistAvatar?: string
  role: string
  invitedAt: string
  expiresAt?: string
}

function getInitials(name: string) {
  if (!name) return '??'
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function getRoleDisplay(role: string) {
  return role === 'manager' ? 'Manager' : role === 'support' ? 'Support' : 'Team Member'
}

export default function TeamHubPage() {
  const navigate = useNavigate()
  const [managedArtists, setManagedArtists] = useState<ManagedArtist[]>([])
  const [invitations, setInvitations] = useState<InvitationRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [respondingTo, setRespondingTo] = useState<string | null>(null)
  const [leavingTeam, setLeavingTeam] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [user] = useState<any>(() => {
    try {
      const raw = localStorage.getItem('dizeeUser')
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })

  const token = (() => {
    try {
      const t = localStorage.getItem('dizeeToken')
      if (!t) return null
      const parsed = JSON.parse(t)
      return typeof parsed === 'string' ? parsed : parsed
    } catch {
      return localStorage.getItem('dizeeToken')
    }
  })()

  const fetchManagedArtists = async () => {
    if (!user?.email || !token) return
    try {
      const res = await postDataAPI('team/managed-artists', { teamMemberEmail: user.email }, token)
      const data = res?.data?.data ?? res?.data ?? res
      setManagedArtists(Array.isArray(data) ? data : [])
    } catch (e) {
      console.error('Error fetching managed artists:', e)
      setError('Failed to load your teams')
    }
  }

  const fetchInvitations = async () => {
    if (!user?.email || !token) return
    try {
      const res = await postDataAPI('team/invitation-requests', { email: user.email }, token)
      const data = res?.data?.data ?? res?.data ?? res
      setInvitations(Array.isArray(data) ? data : [])
    } catch (e) {
      console.error('Error fetching invitations:', e)
    }
  }

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      await Promise.all([fetchManagedArtists(), fetchInvitations()])
      setLoading(false)
    }
    load()
  }, [user?.email])

  const handleAcceptInvite = async (invitation: InvitationRequest) => {
    if (!token) return
    setRespondingTo(invitation._id)
    setError(null)
    try {
      const res = await postDataAPI('team/respond-invitation', { invitationId: invitation._id }, token)
      if (res?.status === 200 || res?.data?.status === 200) {
        setSuccess(`Successfully joined ${invitation.artistName}'s team!`)
        await Promise.all([fetchInvitations(), fetchManagedArtists()])
      } else {
        setError((res?.message ?? res?.data?.message) || 'Failed to accept invitation')
      }
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to accept invitation')
    } finally {
      setRespondingTo(null)
    }
  }

  const handleDeclineInvite = async (invitation: InvitationRequest) => {
    if (!token) return
    setRespondingTo(invitation._id)
    setError(null)
    try {
      const res = await postDataAPI('team/decline-invitation', { invitationId: invitation._id }, token)
      if (res?.status === 200 || res?.data?.status === 200) {
        setSuccess('Invitation declined')
        fetchInvitations()
      } else {
        setError((res?.message ?? res?.data?.message) || 'Failed to decline invitation')
      }
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to decline invitation')
    } finally {
      setRespondingTo(null)
    }
  }

  const handleEnterTeam = (artist: ManagedArtist) => {
    localStorage.setItem('selectedArtist', JSON.stringify(artist))
    navigate('/overview')
    window.location.reload()
  }

  const handleLeaveTeam = async (artist: ManagedArtist) => {
    if (!window.confirm(`Are you sure you want to leave ${artist.name}'s team?`)) return
    if (!token) return
    setLeavingTeam(artist._id)
    setError(null)
    try {
      const res = await postDataAPI(
        'team/leave-team',
        { artistId: artist._id, teamMemberEmail: user?.email },
        token
      )
      if (res?.status === 200 || res?.data?.status === 200) {
        setSuccess(`Left ${artist.name}'s team`)
        fetchManagedArtists()
      } else {
        setError((res?.message ?? res?.data?.message) || 'Failed to leave team')
      }
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to leave team')
    } finally {
      setLeavingTeam(null)
    }
  }

  const managerArtists = managedArtists.filter((a) => a.role === 'manager')
  const supportArtists = managedArtists.filter((a) => a.role === 'support')

  if (!user) {
    return (
      <div className="p-8 max-w-6xl mx-auto">
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      </div>
    )
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-10 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight mb-2 text-gray-900 dark:text-white">
            Team Hub
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage your teams and invitations
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-6">
          <Alert type="error" message={error} onDismiss={() => setError(null)} />
        </div>
      )}
      {success && (
        <div className="mb-6">
          <Alert type="success" message={success} onDismiss={() => setSuccess(null)} />
        </div>
      )}

      {loading ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading your teams…</p>
        </div>
      ) : (
        <>
          {/* Pending Invitations */}
          {invitations.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-10">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                <h2 className="text-sm font-medium text-gray-900 dark:text-white">
                  Pending Invitations ({invitations.length})
                </h2>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {invitations.map((inv) => (
                  <div
                    key={inv._id}
                    className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                        style={{ background: 'linear-gradient(45deg, #6366f1, #8b5cf6)' }}
                      >
                        {inv.artistAvatar ? (
                          <img
                            src={inv.artistAvatar}
                            alt=""
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          getInitials(inv.artistName)
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {inv.artistName}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Invited you as {getRoleDisplay(inv.role)} •{' '}
                          {new Date(inv.invitedAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleAcceptInvite(inv)}
                        disabled={respondingTo === inv._id}
                        className="text-sm py-1 px-3"
                      >
                        {respondingTo === inv._id ? 'Accepting…' : 'Accept'}
                      </Button>
                      <button
                        type="button"
                        onClick={() => handleDeclineInvite(inv)}
                        disabled={respondingTo === inv._id}
                        className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium transition-colors px-3"
                      >
                        {respondingTo === inv._id ? 'Declining…' : 'Decline'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Manager Teams */}
          {managerArtists.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-10">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                <h2 className="text-sm font-medium text-gray-900 dark:text-white">
                  Manager Access ({managerArtists.length})
                </h2>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {managerArtists.map((artist) => (
                  <div
                    key={artist._id}
                    className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                        style={{ background: 'linear-gradient(45deg, #f59e0b, #f97316)' }}
                      >
                        {artist.profilePicture ? (
                          <img
                            src={artist.profilePicture}
                            alt=""
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          getInitials(artist.name)
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {artist.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Full management access
                          {artist.followers ? ` • ${artist.followers.toLocaleString()} followers` : ''}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => handleEnterTeam(artist)} className="text-sm py-1 px-3">
                        Enter
                      </Button>
                      <button
                        type="button"
                        onClick={() => handleLeaveTeam(artist)}
                        disabled={leavingTeam === artist._id}
                        className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium transition-colors px-3"
                      >
                        {leavingTeam === artist._id ? 'Leaving…' : 'Leave'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Support Teams */}
          {supportArtists.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-10">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                <h2 className="text-sm font-medium text-gray-900 dark:text-white">
                  Support Access ({supportArtists.length})
                </h2>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {supportArtists.map((artist) => (
                  <div
                    key={artist._id}
                    className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                        style={{ background: 'linear-gradient(45deg, #06b6d4, #0ea5e9)' }}
                      >
                        {artist.profilePicture ? (
                          <img
                            src={artist.profilePicture}
                            alt=""
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          getInitials(artist.name)
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {artist.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          View-only access
                          {artist.followers ? ` • ${artist.followers.toLocaleString()} followers` : ''}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => handleEnterTeam(artist)} className="text-sm py-1 px-3">
                        Enter
                      </Button>
                      <button
                        type="button"
                        onClick={() => handleLeaveTeam(artist)}
                        disabled={leavingTeam === artist._id}
                        className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium transition-colors px-3"
                      >
                        {leavingTeam === artist._id ? 'Leaving…' : 'Leave'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {managedArtists.length === 0 && invitations.length === 0 && !loading && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">No teams yet</p>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                You'll see team invitations and active teams here once artists invite you.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  )
}
